// Dynamic Sentry import to avoid circular dependencies
// Sentry will be loaded asynchronously after React is ready
import mixpanel from 'mixpanel-browser';
import { logger } from '../utils/logger';
import { isSupabaseAuthDomain } from '../utils/runtimeEnvironment';

// Google Analytics gtag function (loaded via script tag in index.html)
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Types for analytics events
export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
  userId?: string;
}

export interface UserProperties {
  userId: string;
  email?: string;
  fullName?: string;
  isBetaUser?: boolean;
  location?: string;
  joinedAt?: string;
  subscription?: {
    plan?: string;
    status?: string;
  };
}

// Global flag to prevent duplicate Sentry initialization across service workers and main thread
let sentryInitialized = false;
// Store Sentry instance after async load
let Sentry: typeof import('@sentry/react') | null = null;

class MonitoringService {
  private isInitialized = false;
  private isDevelopment = import.meta.env.DEV;

  /**
   * Initialize all monitoring services - NOW ASYNC
   * This must be called after React is loaded to prevent circular dependencies
   */
  async init() {
    if (isSupabaseAuthDomain()) {
      logger.debug('[Monitoring] Skipping initialization on Supabase auth domain');
      return;
    }

    if (this.isInitialized) {
      logger.debug('[Monitoring] Services already initialized, skipping...');
      return;
    }

    try {
      // Initialize Sentry first (async)
      await this.initSentry();

      // Initialize sync services
      this.initGoogleAnalytics();
      this.initMixpanel();

      this.isInitialized = true;
      logger.debug('[Monitoring] Services initialized');
    } catch (error) {
      logger.error('[Monitoring] Failed to initialize:', error);
      // Only capture exception if Sentry is already initialized
      if (sentryInitialized && Sentry) {
        Sentry.captureException(error);
      }
    }
  }

  /**
   * Initialize Sentry error tracking with async import to prevent circular dependencies
   * This loads Sentry AFTER React is ready
   */
  private async initSentry() {
    // Skip Sentry in test environment (Playwright sets navigator.webdriver)
    if (import.meta.env.MODE === 'test' ||
        (typeof navigator !== 'undefined' && navigator.webdriver)) {
      logger.debug('[Monitoring] Skipping Sentry in test environment');
      return;
    }

    // Global singleton check to prevent duplicate instances
    if (sentryInitialized) {
      logger.debug('[Monitoring] Sentry already initialized globally, skipping...');
      return;
    }

    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) {
      logger.warn('[Monitoring] Sentry DSN not configured');
      return;
    }

    try {
      // Dynamic import of Sentry AFTER React is loaded
      Sentry = await import('@sentry/react');

      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: this.isDevelopment ? 1.0 : 0.1,
        // Session Replay
        replaysSessionSampleRate: this.isDevelopment ? 1.0 : 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Release tracking
        release: import.meta.env.VITE_SENTRY_RELEASE || 'vitly@1.0.0',
        beforeSend(event) {
          // Filter out development errors
          if (import.meta.env.DEV) {
            return event;
          }
          // Filter out known non-critical errors
          if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop limit exceeded')) {
            return null;
          }
          return event;
        },
      });

      // Mark Sentry as initialized globally
      sentryInitialized = true;
      logger.debug('[Monitoring] Sentry initialized successfully with async import');
    } catch (error) {
      logger.error('[Monitoring] Failed to dynamically load Sentry:', error);
      throw error;
    }
  }

  /**
   * Initialize Google Analytics 4
   */
  private initGoogleAnalytics() {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) {
      logger.warn('[Monitoring] Google Analytics measurement ID not configured');
      return;
    }

    const hostname = window.location.hostname;
    const rawPath = window.location.pathname;
    const pathname = rawPath.endsWith('/') && rawPath.length > 1 ? rawPath.slice(0, -1) : rawPath;
    const isProductionHost = hostname === 'getvitly.com' || hostname === 'www.getvitly.com';
    const marketingPaths = new Set([
      '/',
      '/pricing',
      '/features',
      '/how-it-works',
      '/about',
      '/contact',
      '/blog',
      '/invitation',
      '/beta-request',
      '/data-deletion',
      '/privacy',
      '/terms'
    ]);

    if (!isProductionHost || !marketingPaths.has(pathname)) {
      logger.debug('[Monitoring] Skipping Google Analytics for path:', pathname, 'host:', hostname);
      return;
    }

    const respectsPrivacy = typeof navigator !== 'undefined' && (
      navigator.doNotTrack === '1' ||
      (window as any).doNotTrack === '1' ||
      (navigator as any).msDoNotTrack === '1' ||
      (navigator as any).globalPrivacyControl === true
    );

    if (respectsPrivacy) {
      logger.info('[Monitoring] Skipping Google Analytics due to user privacy preferences');
      return;
    }

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onerror = () => {
      logger.warn('[Monitoring] Google Analytics script failed to load or was blocked');
    };
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = (...args: unknown[]) => {
      (window as any).dataLayer.push(args);
    };

    (window as any).gtag('js', new Date());
    (window as any).gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href,
      debug_mode: this.isDevelopment,
      send_page_view: true,
    });
  }

  /**
   * Initialize Mixpanel analytics
   */
  private initMixpanel() {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN;
    if (!token) {
      // Silently skip Mixpanel initialization if token not configured (it's optional)
      if (this.isDevelopment) {
        console.debug('Mixpanel token not configured - skipping initialization');
      }
      return;
    }

    mixpanel.init(token, {
      debug: this.isDevelopment,
      track_pageview: true,
      persistence: 'localStorage',
      property_blacklist: ['$current_url', '$initial_referrer', '$referrer'],
      // GDPR compliance
      opt_out_tracking_by_default: false,
      ignore_dnt: false,
    });
  }

  /**
   * Set user context for all monitoring services
   */
  setUser(user: UserProperties) {
    // Sentry user context (only if Sentry is loaded)
    if (Sentry && sentryInitialized) {
      Sentry.setUser({
        id: user.userId,
        email: user.email,
        username: user.fullName,
        extra: {
          isBetaUser: user.isBetaUser,
          location: user.location,
          joinedAt: user.joinedAt,
          subscription: user.subscription,
        },
      });
    }

    // Google Analytics user properties
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        user_id: user.userId,
        custom_map: {
          user_type: user.isBetaUser ? 'beta' : 'regular',
          subscription_plan: user.subscription?.plan || 'free',
        },
      });
    }

    // Mixpanel user profile
    if (mixpanel) {
      mixpanel.identify(user.userId);
      mixpanel.people.set({
        $email: user.email,
        $name: user.fullName,
        $created: user.joinedAt,
        is_beta_user: user.isBetaUser,
        location: user.location,
        subscription_plan: user.subscription?.plan || 'free',
        subscription_status: user.subscription?.status || 'free',
      });
    }
  }

  /**
   * Clear user context (logout)
   */
  clearUser() {
    if (Sentry && sentryInitialized) {
      Sentry.setUser(null);
    }

    if (mixpanel) {
      mixpanel.reset();
    }
  }

  /**
   * Track custom events
   */
  trackEvent(event: AnalyticsEvent) {
    const { name, properties = {}, userId } = event;

    // Add common properties
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      user_id: userId,
    };

    // Sentry breadcrumb (only if Sentry is loaded)
    if (Sentry && sentryInitialized) {
      Sentry.addBreadcrumb({
        category: 'user-action',
        message: name,
        data: enrichedProperties,
        level: 'info',
      });
    }

    // Google Analytics event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', name, {
        event_category: properties.category || 'user_interaction',
        event_label: properties.label,
        value: properties.value,
        custom_parameters: enrichedProperties,
      });
    }

    // Mixpanel event
    if (mixpanel) {
      mixpanel.track(name, enrichedProperties);
    }

    // Development logging
    if (this.isDevelopment) {
      logger.debug('[Analytics] Event', { name, properties: enrichedProperties });
    }
  }

  /**
   * Track page views
   */
  trackPageView(page: string, title?: string) {
    const properties = {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: page,
    };

    // Google Analytics page view
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        page_title: properties.page_title,
        page_location: properties.page_location,
      });
    }

    // Mixpanel page view
    if (mixpanel) {
      mixpanel.track('Page View', properties);
    }

    // Sentry navigation breadcrumb (only if Sentry is loaded)
    if (Sentry && sentryInitialized) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${page}`,
        data: properties,
        level: 'info',
      });
    }
  }

  /**
   * Track errors manually
   */
  trackError(error: Error, context?: Record<string, unknown>) {
    if (Sentry && sentryInitialized) {
      Sentry.withScope((scope) => {
        if (context) {
          scope.setContext('error_context', context);
        }
        if (Sentry) {
          Sentry.captureException(error);
        }
      });
    }

    // Track as event for analytics
    this.trackEvent({
      name: 'Error Occurred',
      properties: {
        error_message: error.message,
        error_stack: error.stack,
        ...context,
      },
    });
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric: string, value: number, context?: Record<string, unknown>) {
    const properties = {
      metric_name: metric,
      metric_value: value,
      ...context,
    };

    // Google Analytics custom metric
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'performance_metric', {
        event_category: 'performance',
        event_label: metric,
        value: Math.round(value),
        custom_parameters: properties,
      });
    }

    // Mixpanel performance tracking
    if (mixpanel) {
      mixpanel.track('Performance Metric', properties);
    }
  }

  /**
   * Track conversion events
   */
  trackConversion(event: string, value?: number, currency = 'USD') {
    const properties = {
      event_category: 'conversion',
      currency,
      value,
    };

    // Google Analytics conversion
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: import.meta.env.VITE_GA_MEASUREMENT_ID,
        event_category: 'conversion',
        event_label: event,
        value,
        currency,
      });
    }

    // Mixpanel conversion
    if (mixpanel) {
      mixpanel.track(`Conversion: ${event}`, properties);
    }

    // Track as general event
    this.trackEvent({
      name: `Conversion: ${event}`,
      properties,
    });
  }
}

// Export singleton instance
export const monitoring = new MonitoringService();

// Export common tracking functions
export const trackEvent = (event: AnalyticsEvent) => monitoring.trackEvent(event);
export const trackPageView = (page: string, title?: string) => monitoring.trackPageView(page, title);
export const trackError = (error: Error, context?: Record<string, unknown>) => monitoring.trackError(error, context);
export const trackPerformance = (metric: string, value: number, context?: Record<string, unknown>) =>
  monitoring.trackPerformance(metric, value, context);
export const trackConversion = (event: string, value?: number, currency?: string) =>
  monitoring.trackConversion(event, value, currency);
export const setUser = (user: UserProperties) => monitoring.setUser(user);
export const clearUser = () => monitoring.clearUser();

export default monitoring;
